import { Brain, Cloud, BarChart3, Shield, Workflow, Smartphone, LayoutGrid, Database, Users, Landmark, Server, Cpu, Network, Boxes, Sparkles, Zap, Lock, Rocket, Lightbulb, Globe2 } from 'lucide-react';

export const SITE = {
    name: 'DigiTech Cloud AI',
    tagline: 'Advanced Software Engineering, AI & Cloud',
    description: 'An advanced technology and software-engineering partner delivering secure, intelligent, and scalable digital systems for governments, enterprises, and ambitious ventures.',
    email: 'info@digitechcloud-ai.com',
    phone: '+1 (307) 200-0734',
    address: '1309 Coffeen Avenue, Suite 1200, Sheridan, Wyoming 82801, USA',
    hours: 'Consultations by appointment',
    uk_subsidiary: 'United Kingdom subsidiary: Metatron Technologies Ltd',
};

// Hero four proof/statistic cards — non-numeric, qualitative
export const STATS = [
    { value: 'END-TO-END',   label: 'Delivery Ownership',   sub: 'Strategy through operation' },
    { value: 'MULTI-SECTOR', label: 'Technology Expertise', sub: 'Public and private sectors' },
    { value: 'GLOBAL',       label: 'Delivery Perspective', sub: 'Africa-rooted, globally connected' },
    { value: 'LONG-TERM',    label: 'Partnership Approach', sub: 'Knowledge transfer and continuous improvement' },
];

// Icon lookup used to resolve icon names from CMS content
export const ICONS = {
    Brain, Cloud, BarChart3, Shield, Workflow, Smartphone, LayoutGrid, Database, Users, Landmark, Server, Cpu, Network, Boxes, Sparkles, Zap, Lock, Rocket, Lightbulb, Globe2,
};

export const CAPABILITIES = [
    {
        key: 'engineering', icon: Boxes, eyebrow: 'SOFTWARE ENGINEERING',
        title: 'Advanced Programming & Architecture',
        body: 'We engineer the digital core—not merely the interface—using secure, modular, maintainable architectures built for complex workflows, integration, performance, and long-term scale.',
        bullets: ['Enterprise web, mobile, and backend systems', 'APIs, microservices, and integration layers', 'Real-time, event-driven, and multi-tenant platforms'],
    },
    {
        key: 'ai', icon: Brain, eyebrow: 'AI & DATA',
        title: 'Artificial Intelligence & Data Intelligence',
        body: 'We apply AI where it improves a measurable decision, process, or user experience, supported by trusted data, responsible governance, human oversight, and robust software engineering.',
        bullets: ['Enterprise assistants, RAG, and AI agents', 'Predictive analytics and intelligent automation', 'Data pipelines, dashboards, and forecasting'],
    },
    {
        key: 'cloud', icon: Cloud, eyebrow: 'CLOUD & DEVOPS',
        title: 'Cloud Platforms & Reliable Operations',
        body: 'We design cloud-native, hybrid, and distributed environments with deployment automation, observability, resilience, and cost-conscious scaling built into the operating model.',
        bullets: ['Containers and deployment automation', 'Monitoring, logging, and performance engineering', 'Backup, recovery, and continuity design'],
    },
    {
        key: 'security', icon: Shield, eyebrow: 'CYBERSECURITY',
        title: 'Security, Privacy & Resilience',
        body: 'Security and privacy are designed into the architecture, development lifecycle, access model, data flows, and operational controls from the beginning.',
        bullets: ['Identity, access, and encryption controls', 'Secure development and vulnerability management', 'Audit trails and incident-readiness design'],
    },
    {
        key: 'connected', icon: Network, eyebrow: 'CONNECTED SYSTEMS',
        title: 'IoT, Edge & Automation',
        body: 'We connect devices, data, applications, and field operations to improve visibility, coordinate action, automate routine work, and support faster response.',
        bullets: ['Connected devices and edge processing', 'Workflow orchestration and real-time alerts', 'Human-supervised automation and operational intelligence'],
    },
];

// Home services preview + Services page cards
export const SERVICES = [
    { key: 'engineering', icon: Boxes,     tag: 'ENGINEERING',   title: 'Advanced Software Engineering',
      body: 'Custom web, mobile, backend, SaaS, API, microservice, and real-time systems engineered for security, performance, interoperability, and scale.',
      proof: ['Enterprise applications and complex business logic', 'APIs, microservices, middleware, and integrations', 'Real-time, event-driven, and high-throughput systems'] },
    { key: 'ai',          icon: Brain,     tag: 'AI & DATA',     title: 'AI & Data Intelligence',
      body: 'Responsible AI assistants, document intelligence, predictive analytics, data platforms, dashboards, and intelligent automation embedded into real workflows.',
      proof: ['Enterprise assistants, RAG, and AI agents', 'Predictive analytics, forecasting, and anomaly detection', 'Data platforms, pipelines, dashboards, and business intelligence'] },
    { key: 'enterprise',  icon: Database,  tag: 'ENTERPRISE',    title: 'Enterprise Platforms',
      body: 'ERP, CRM, HR, finance, billing, workflow, case-management, document, and executive-intelligence platforms tailored to organizational needs.',
      proof: ['ERP, CRM, HR, finance, billing, and invoicing', 'Workflow, document, archive, and case management', 'Executive dashboards and decision-support portals'] },
    { key: 'cloud',       icon: Cloud,     tag: 'CLOUD',         title: 'Cloud & DevOps',
      body: 'Cloud-native and hybrid architecture, containers, automated deployment, observability, resilience, performance optimisation, and managed operations.',
      proof: ['Containers, orchestration, and infrastructure automation', 'Monitoring, logging, alerting, and optimisation', 'Backup, disaster recovery, and continuity design'] },
    { key: 'security',    icon: Shield,    tag: 'CYBERSECURITY', title: 'Cybersecurity & Privacy',
      body: 'Secure-by-design architecture, identity and access controls, encryption, auditability, vulnerability management, privacy controls, and incident readiness.',
      proof: ['Identity, role-based access, and least privilege', 'Encryption, audit trails, and secure development', 'Vulnerability management and incident readiness'] },
    { key: 'govtech',     icon: Landmark,  tag: 'PUBLIC SECTOR', title: 'GovTech & Digital Transformation',
      body: 'Digital administration, citizen services, public finance, institutional workflows, connectivity, public-safety systems, and executive dashboards.',
      proof: ['Digital administration and citizen-service platforms', 'Legacy modernisation and systems integration', 'Connectivity, field operations, and executive intelligence'] },
];

// Home marquee ticker terms
export const TECH_TICKER = [
    'ADVANCED PROGRAMMING', 'SOFTWARE ARCHITECTURE', 'WEB & MOBILE', 'SAAS PLATFORMS',
    'APIS & MICROSERVICES', 'SYSTEMS INTEGRATION', 'ARTIFICIAL INTELLIGENCE', 'DATA ENGINEERING',
    'CLOUD & DEVOPS', 'CYBERSECURITY', 'IOT & EDGE', 'REAL-TIME SYSTEMS',
];

// Kept for the tech-stack tile grid (icons + labels)
export const TECH_STACK = [
    { icon: Boxes,    label: 'Advanced Programming' },
    { icon: Cpu,      label: 'Software Architecture' },
    { icon: Smartphone, label: 'Web & Mobile' },
    { icon: LayoutGrid, label: 'SaaS Platforms' },
    { icon: Workflow, label: 'APIs & Microservices' },
    { icon: Brain,    label: 'Artificial Intelligence' },
    { icon: Database, label: 'Data Engineering' },
    { icon: Cloud,    label: 'Cloud & DevOps' },
    { icon: Shield,   label: 'Cybersecurity' },
    { icon: Network,  label: 'IoT & Edge' },
    { icon: Zap,      label: 'Real-time Systems' },
    { icon: Server,   label: 'Systems Integration' },
];

// Contact form select options
export const ROLES = [
    'Executive / Business Leader',
    'Government / Public-Sector Leader',
    'Founder / Entrepreneur',
    'Technology / Engineering Leader',
    'Product / Data Leader',
    'Operations / Transformation Leader',
    'Procurement / Partnerships',
    'Other',
];
export const PROJECT_TYPES = [
    'Advanced Software Engineering',
    'AI & Data Intelligence',
    'Enterprise Platform / SaaS',
    'Cloud & DevOps',
    'Cybersecurity & Privacy',
    'GovTech / Digital Transformation',
    'Systems Integration / Modernisation',
    'IoT / Edge / Automation',
    'Advisory / Strategic Partnership',
    'Other',
];
export const BUDGETS = ['Not yet defined', '< $25k', '$25k – $75k', '$75k – $250k', '$250k – $1M', '$1M+'];
